import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trash2, AlertTriangle, CheckCircle, Link as LinkIcon,
  MessageCircle, Image as ImageIcon, Copy, Pencil, Plus, X, Upload,
  ChevronDown, ChevronRight, FileText, Download, Hotel
} from 'lucide-react';
import api from '../../services/api';
import VendorDropdown from '../ui/VendorDropdown';
import SearchableSelect from '../ui/SearchableSelect';
import FileViewerModal from '../common/FileViewerModal';
import jsPDF from 'jspdf';
import { useAuth } from '../../contexts/AuthContext';

const DRIVER_VENDOR_ID = 840;

const STATUS_OPTIONS = [
  { value: 'NOT_BOOKED', label: 'Not Booked' },
  { value: 'BOOKED', label: 'Booked' },
  { value: 'PENDING_PAYMENT', label: 'Pending Payment' },
  { value: 'PAYMENT_COMPLETED', label: 'Payment Completed' },
  { value: 'CANCELED_NO_FEE', label: 'Canceled (No Fee)' },
  { value: 'CANCELED_WITH_FEE', label: 'Canceled (With Fee)' }
];
const DUE_DATE_STATUSES = ['PENDING_PAYMENT', 'PAYMENT_COMPLETED', 'CANCELED_WITH_FEE'];

interface LedgerItem {
  id: number;
  name: string;
  item_type: 'prebooked' | 'driver_paid';
  item_subtype?: string;
  day: number;
  cost: number;
  parking: number;
  verification_status: string | null;
  vendor: string;
  booking_status: string;
  due_date: string | null;
  activity_id?: number | null;
  meal_id?: number | null;
  booking_reference?: string | null;
  remarks?: string | null;
  activity_date?: string | null;
  calculated_adults?: number;
  calculated_kids?: number;
}

interface AccommodationItem {
  id: number;
  hotel_name: string;
  room_type: string;
  checkin_date: string | null;
  checkout_date: string | null;
  no_of_nights: number;
  no_of_rooms: number;
  cost: number;
  vendor: string;
  booking_status: string;
  due_date: string | null;
  booking_reference?: string | null;
  remarks?: string | null;
}

interface VendorSummary {
  vendor_id: number | null;
  vendor_name: string;
  item_types: string[];
  total_paid: number;
  payments: { payment_date: string; payment_amount: number; bank_account: string | null; payment_reference: string | null }[];
  invoices: { filename: string; s3_key: string }[];
  receipts: { filename: string; s3_key: string }[];
}

interface TransportDay {
  id: number;
  day: number;
  trip: string;
  trip_cost: number;
  overtime: number;
  parking: number; // authoritative, persisted value — sum of that day's per-item entries below
}

interface DriverPayment {
  payment_date: string;
  payment_amount: number;
  payment_reference?: string;
  bank_account?: string;
}

interface MasterData {
  activities: Record<string, number>;
  meals: Record<string, number>;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n || 0);

const soraFont: React.CSSProperties = { fontFamily: "'Sora', ui-sans-serif, sans-serif" };
const monoFont: React.CSSProperties = { fontFamily: "'IBM Plex Mono', ui-monospace, monospace" };

// ---------------------------------------------------------------------------
// Vendor files panel — invoices & receipts are vendor-level documents: one
// invoice can cover several items booked with the same vendor (e.g. snorkeling
// + banana boat), so files are managed here per vendor, not inside the per-item
// modal. Same endpoints BookingModal's FileUploadSection uses. Driver Paid
// items share the "prebooked_activities" section (no dedicated one on the API).
// ---------------------------------------------------------------------------
type FileSection = 'prebooked_activities' | 'accommodation';
interface VendorRef { vendor: string; section: FileSection; }
interface VendorFile {
  key: string; name: string; url: string; downloadUrl?: string;
  vendor: string; source: 'operational' | 'receipt'; uploadDate?: string | null;
  _section: FileSection;
}

const normVendor = (s: string) => (s || '').trim().toLowerCase();

// All money inputs in this screen are non-negative — clamp on entry to avoid
// mistakes (typed "-", pasted negatives, spinner going below zero).
const nonNeg = (v: string) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const UploadButton: React.FC<{
  label: string; subtle?: boolean; disabled?: boolean; onPick: (f: File) => void;
}> = ({ label, subtle, disabled, onPick }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg border disabled:opacity-50 ${
          subtle ? 'border-gray-300 text-gray-600 hover:border-gray-400'
                 : 'border-blue-500 text-blue-600 hover:bg-blue-50'
        }`}
      >
        <Upload className="h-3.5 w-3.5" /> {disabled ? 'Uploading…' : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
        className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }}
      />
    </>
  );
};

const FileList: React.FC<{
  title: string; files: VendorFile[]; busyKey: string | null; onDelete: (f: VendorFile) => void; onView: (f: VendorFile) => void;
}> = ({ title, files, busyKey, onDelete, onView }) => {
  if (files.length === 0) {
    return title ? <p className="text-xs text-gray-400 mb-1">{title}: none</p> : null;
  }
  return (
    <div className="mb-1">
      {title && <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{title}</p>}
      <div className="space-y-1">
        {files.map(f => (
          <div key={f.key} className="flex items-center gap-2 text-xs">
            <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <button
              type="button"
              onClick={() => onView(f)}
              className="text-blue-600 hover:underline truncate flex-1 text-left"
            >
              {f.name}
            </button>
            {f.downloadUrl && (
              <a href={f.downloadUrl} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-gray-600" title="Download">
                <Download className="h-3.5 w-3.5" />
              </a>
            )}
            <button
              onClick={() => onDelete(f)}
              disabled={busyKey === f.key}
              className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const VendorFilesPanel: React.FC<{ itineraryId: string; vendors: VendorRef[] }> = ({ itineraryId, vendors }) => {
  const [open, setOpen] = useState(true);
  const [files, setFiles] = useState<VendorFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ isOpen: boolean; url: string; name: string; type: 'image' | 'pdf' }>({
    isOpen: false, url: '', name: '', type: 'image'
  });

  const openViewer = (f: VendorFile) => {
    const ext = f.name.toLowerCase().split('.').pop() || '';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
      setViewer({ isOpen: true, url: f.url, name: f.name, type: 'image' });
    } else if (ext === 'pdf') {
      setViewer({ isOpen: true, url: f.url, name: f.name, type: 'pdf' });
    } else {
      // Unsupported in the inline viewer (e.g. .doc/.docx) — fall back to a new tab
      window.open(f.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Always look in both sections regardless of what vendors the ledger knows
  // about — a hotel's files are filed under the hotel name, which may not match
  // the accommodation row's vendor field.
  const sections: FileSection[] = ['prebooked_activities', 'accommodation'];

  const loadFiles = async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        sections.map(s =>
          api.get(`/operational-cost/${itineraryId}/files/${s}`)
            .then(r => (r.data.success ? (r.data.data as VendorFile[]) : []))
            .then(list => list.map(f => ({ ...f, _section: s })))
            .catch(() => [] as VendorFile[])
        )
      );
      const merged = new Map<string, VendorFile>();
      results.flat().forEach(f => merged.set(f.key, f));
      setFiles(Array.from(merged.values()));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  const upload = async (ref: VendorRef, file: File, fileType: 'operational' | 'receipt') => {
    const bk = normVendor(ref.vendor) + '|' + ref.section;
    setBusyKey(bk);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('section', ref.section);
      fd.append('file_type', fileType);
      await api.post(
        `/operational-cost/${itineraryId}/upload?vendor=${encodeURIComponent(ref.vendor)}`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      await loadFiles();
    } catch (err) {
      console.error('Error uploading vendor file:', err);
      alert('Failed to upload file. Please try again.');
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (f: VendorFile) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    setBusyKey(f.key);
    try {
      await api.delete(`/operational-cost/${itineraryId}/files/${encodeURIComponent(f.key)}`);
      await loadFiles();
    } catch (err) {
      console.error('Error deleting vendor file:', err);
      alert('Failed to delete file. Please try again.');
    } finally {
      setBusyKey(null);
    }
  };

  // One row per (section, vendor): the union of vendors the ledger knows about
  // and vendors that already have files on record (so a hotel's docs land under
  // the hotel, not in an "orphan" bucket).
  const rows = useMemo<VendorRef[]>(() => {
    const map = new Map<string, VendorRef>();
    vendors.forEach(v => {
      if (v.vendor.trim()) map.set(v.section + '|' + normVendor(v.vendor), { vendor: v.vendor.trim(), section: v.section });
    });
    files.forEach(f => {
      const name = (f.vendor || '').trim();
      if (!name) return;
      const k = f._section + '|' + normVendor(name);
      if (!map.has(k)) map.set(k, { vendor: name, section: f._section });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.section === b.section ? a.vendor.localeCompare(b.vendor) : a.section.localeCompare(b.section)
    );
  }, [vendors, files]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left">
        <div>
          <h2 className="font-semibold text-gray-900" style={soraFont}>Vendor Invoices &amp; Files</h2>
          <p className="text-xs text-gray-500">
            One invoice can cover several items booked with the same vendor — manage them here, not per item.
          </p>
        </div>
        <div className="flex items-center gap-2 text-gray-400">
          <span className="text-xs">{files.length} file{files.length === 1 ? '' : 's'}</span>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {rows.length === 0 && (
            <div className="p-5 text-sm text-gray-500 text-center">
              Assign a vendor to an activity or hotel to attach its invoice.
            </div>
          )}
          {loading && <div className="p-4 text-xs text-gray-400">Loading files…</div>}

          {rows.map(ref => {
            const bk = normVendor(ref.vendor) + '|' + ref.section;
            const vf = files.filter(f => f._section === ref.section && normVendor(f.vendor) === normVendor(ref.vendor));
            const invoices = vf.filter(f => f.source === 'operational');
            const receipts = vf.filter(f => f.source === 'receipt');
            return (
              <div key={bk} className="p-4 sm:p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{ref.vendor}</span>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                      {ref.section === 'accommodation' ? 'Accommodation' : 'Activities'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <UploadButton label="Add invoice" disabled={busyKey === bk} onPick={f => upload(ref, f, 'operational')} />
                    <UploadButton label="Add receipt" subtle disabled={busyKey === bk} onPick={f => upload(ref, f, 'receipt')} />
                  </div>
                </div>
                <FileList title="Invoices" files={invoices} busyKey={busyKey} onDelete={remove} onView={openViewer} />
                <FileList title="Receipts" files={receipts} busyKey={busyKey} onDelete={remove} onView={openViewer} />
              </div>
            );
          })}
        </div>
      )}

      <FileViewerModal
        isOpen={viewer.isOpen}
        onClose={() => setViewer(v => ({ ...v, isOpen: false }))}
        fileUrl={viewer.url}
        fileName={viewer.name}
        fileType={viewer.type}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Item modal — create (per day) or edit an existing prebooked/driver_paid item.
// Vendor/Status/Due Date/Invoice live here, not inline in the ledger row.
// ---------------------------------------------------------------------------
interface ItemModalState { mode: 'create' | 'edit'; day: number; item?: LedgerItem; }

const ItemModal: React.FC<{
  state: ItemModalState;
  itineraryId: string;
  masterData: MasterData | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ state, itineraryId, masterData, onClose, onSaved }) => {
  const isCreate = state.mode === 'create';
  const [itemType, setItemType] = useState<'prebooked' | 'driver_paid'>(state.item?.item_type || 'prebooked');
  const [subtype, setSubtype] = useState(state.item?.item_subtype || 'addon');
  const [name, setName] = useState(state.item?.name || '');
  const [activityId, setActivityId] = useState<number | null>(state.item?.activity_id || null);
  const [mealId, setMealId] = useState<number | null>(state.item?.meal_id || null);
  const [cost, setCost] = useState(String(state.item?.cost ?? ''));
  const [parking, setParking] = useState(String(state.item?.parking ?? ''));
  const [vendor, setVendor] = useState(state.item?.vendor || (itemType === 'driver_paid' ? 'Driver' : ''));
  const [bookingStatus, setBookingStatus] = useState(state.item?.booking_status || 'NOT_BOOKED');
  const [dueDate, setDueDate] = useState(state.item?.due_date || '');
  const [remarks, setRemarks] = useState(state.item?.remarks || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const activityOptions = useMemo(
    () => Object.entries(masterData?.activities || {}).map(([label, id]) => ({ value: String(id), label })),
    [masterData]
  );
  const mealOptions = useMemo(
    () => Object.entries(masterData?.meals || {}).map(([label, id]) => ({ value: String(id), label })),
    [masterData]
  );

  const handleStatusChange = (value: string) => {
    setBookingStatus(value);
    if (value === 'BOOKED' || value === 'NOT_BOOKED') setDueDate('');
  };

  const handleSubmit = async () => {
    setError('');
    if (!name.trim()) { setError('Name is required.'); return; }
    if (subtype === 'activity' && !activityId) { setError('Please select an activity.'); return; }
    if (subtype === 'meal' && !mealId) { setError('Please select a meal.'); return; }

    setSaving(true);
    try {
      if (isCreate) {
        await api.post('/booking/create-item-v2', {
          itinerary_id: Number(itineraryId),
          item_type: itemType === 'prebooked' ? 'PREBOOKED' : 'DRIVER_PAID',
          item_subtype: subtype,
          name,
          day_number: state.day,
          estimated_amount: nonNeg(cost),
          booking_status: bookingStatus,
          vendor_name: vendor || null,
          remarks: remarks || null,
          due_date: dueDate || null,
          activity_id: subtype === 'activity' ? activityId : null,
          meal_id: subtype === 'meal' ? mealId : null
        });
      } else if (state.item) {
        const res = await api.put('/booking/bulk-update-items-v2', [{
          id: state.item.id,
          name,
          cost: nonNeg(cost),
          parking: nonNeg(parking),
          item_type: itemType,
          vendor_name: vendor || null,
          booking_status: bookingStatus,
          due_date: dueDate || null,
          remarks: remarks || null
        }]);
        if (!res.data.success) {
          setError('Some fields failed to save. Check the console for details.');
          console.error('Bulk update errors:', res.data.errors);
          setSaving(false);
          return;
        }
      }
      onSaved();
    } catch (err) {
      console.error('Error saving item:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-base font-bold text-gray-900" style={soraFont}>
            {isCreate ? `Add Item — Day ${state.day}` : 'Edit Item'}
          </h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-2.5">{error}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={itemType} onChange={e => setItemType(e.target.value as 'prebooked' | 'driver_paid')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="prebooked">Prebooked</option>
                <option value="driver_paid">Driver paid</option>
              </select>
            </div>
            {isCreate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item is a…</label>
                <select value={subtype} onChange={e => { setSubtype(e.target.value); setActivityId(null); setMealId(null); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="addon">Custom item</option>
                  <option value="activity">Activity (from catalog)</option>
                  <option value="meal">Meal (from catalog)</option>
                </select>
              </div>
            )}
          </div>

          {isCreate && subtype === 'activity' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Activity</label>
              <SearchableSelect
                options={activityOptions}
                value={activityId ? String(activityId) : ''}
                onChange={v => { const id = Number(v); setActivityId(id); const opt = activityOptions.find(o => o.value === String(v)); if (opt) setName(opt.label); }}
                placeholder="Search activities…"
              />
            </div>
          )}
          {isCreate && subtype === 'meal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meal</label>
              <SearchableSelect
                options={mealOptions}
                value={mealId ? String(mealId) : ''}
                onChange={v => { const id = Number(v); setMealId(id); const opt = mealOptions.find(o => o.value === String(v)); if (opt) setName(opt.label); }}
                placeholder="Search meals…"
              />
            </div>
          )}
          {(!isCreate || subtype === 'addon') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Item name" />
            </div>
          )}

          <div className={isCreate ? '' : 'grid grid-cols-2 gap-3'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
              <input type="text" inputMode="decimal" value={cost}
                onChange={e => setCost(e.target.value.replace(/[^\d.]/g, '').replace(/(\.\d*)\./g, '$1'))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            {!isCreate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parking</label>
                <input type="text" inputMode="decimal" value={parking}
                  onChange={e => setParking(e.target.value.replace(/[^\d.]/g, '').replace(/(\.\d*)\./g, '$1'))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <VendorDropdown value={vendor} onChange={setVendor} placeholder="Select vendor" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={bookingStatus} onChange={e => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {DUE_DATE_STATUSES.includes(bookingStatus) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" value={dueDate || ''} onChange={e => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            )}
          </div>

          {!isCreate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Internal notes…" />
            </div>
          )}

          {!isCreate && (
            <p className="text-[11px] text-gray-400">
              Driver Paid items can also be edited directly on the ledger row.
              Invoices are managed per vendor in the “Vendor Invoices &amp; Files” panel on the booking page.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : isCreate ? 'Add Item' : 'Save Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Accommodation modal — hotel_name/room_type/dates/nights/rooms are read-only
// here too (matching the existing Manage Booking — they're not editable there
// either); cost/vendor/status/due-date/invoice are.
// ---------------------------------------------------------------------------
const AccommodationModal: React.FC<{
  item: AccommodationItem;
  itineraryId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ item, itineraryId, onClose, onSaved }) => {
  const [cost, setCost] = useState(String(item.cost ?? ''));
  const [vendor, setVendor] = useState(item.vendor || '');
  const [bookingStatus, setBookingStatus] = useState(item.booking_status || 'NOT_BOOKED');
  const [dueDate, setDueDate] = useState(item.due_date || '');
  const [remarks, setRemarks] = useState(item.remarks || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleStatusChange = (value: string) => {
    setBookingStatus(value);
    if (value === 'BOOKED' || value === 'NOT_BOOKED') setDueDate('');
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await api.put('/booking/bulk-update-items-v2', [{
        id: item.id, cost: nonNeg(cost), vendor_name: vendor || null, booking_status: bookingStatus,
        due_date: dueDate || null, remarks: remarks || null
      }]);
      if (!res.data.success) {
        setError('Some fields failed to save.');
        console.error('Bulk update errors:', res.data.errors);
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      console.error('Error saving accommodation:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-base font-bold text-gray-900" style={soraFont}>{item.hotel_name}</h3>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-2.5">{error}</div>}

          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 space-y-1">
            <p><b className="text-gray-800">{item.room_type}</b> · {item.no_of_nights} nights · {item.no_of_rooms} rooms</p>
            <p>{item.checkin_date || '—'} → {item.checkout_date || '—'}</p>
            <p className="text-[11px] text-gray-400">Hotel/room/dates aren't editable here — same as today's Manage Booking.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
            <input type="text" inputMode="decimal" value={cost}
              onChange={e => setCost(e.target.value.replace(/[^\d.]/g, '').replace(/(\.\d*)\./g, '$1'))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <VendorDropdown value={vendor} onChange={setVendor} placeholder="Select vendor" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={bookingStatus} onChange={e => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {DUE_DATE_STATUSES.includes(bookingStatus) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" value={dueDate || ''} onChange={e => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Internal notes…" />
          </div>

          <p className="text-[11px] text-gray-400">
            Invoices are managed per vendor in the “Vendor Invoices &amp; Files” panel on the booking page.
          </p>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Details'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const ManageBooking: React.FC = () => {
  const { itineraryId } = useParams<{ itineraryId: string }>();
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const canViewOpsReport = userRole === 'ADMIN' || userRole === 'WYNDER_ACCOUNTS';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'ledger' | 'accommodation' | 'ops_report'>('ledger');

  const [meta, setMeta] = useState<{ guest: string; agency: string; pax: number; children: number; childrenAges: (string | number)[]; driverName: string; travelDate: string; numDays: number; totalEstimatedCost: number } | null>(null);
  const [items, setItems] = useState<LedgerItem[]>([]);
  const [accommodations, setAccommodations] = useState<AccommodationItem[]>([]);
  const [transportDays, setTransportDays] = useState<TransportDay[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  // Show every ledger row, or just the ones the driver was actually paid for.
  const [itemFilter, setItemFilter] = useState<'all' | 'driver_paid'>('all');
  const [dirtyItemIds, setDirtyItemIds] = useState<Set<number>>(new Set());
  const [dirtyTransportIds, setDirtyTransportIds] = useState<Set<number>>(new Set());
  // Days whose per-item parking inputs were touched — on Save, each day's items are
  // summed and that sum is written to the day's Transportation row (see handleSave).
  const [parkingDirtyDays, setParkingDirtyDays] = useState<Set<number>>(new Set());
  // In-progress text for the money cells (plain text inputs, so arrow keys /
  // scroll can't nudge the value). Cleared on blur / reload so it snaps back
  // to the canonical number.
  const [numDrafts, setNumDrafts] = useState<Record<string, string>>({});

  const [driverPayments, setDriverPayments] = useState<DriverPayment[]>([]);
  const [vendorSummary, setVendorSummary] = useState<VendorSummary[]>([]);
  const [itemModal, setItemModal] = useState<ItemModalState | null>(null);
  const [accModalItem, setAccModalItem] = useState<AccommodationItem | null>(null);

  // Driver assignment (was the old Transportation tab). Saved via
  // /booking/update-driver-name, which stamps the name + status onto every
  // driver_paid + transportation item.
  const [driverName, setDriverName] = useState('');
  const [driverStatus, setDriverStatus] = useState('NOT_BOOKED');
  const [driverBaseline, setDriverBaseline] = useState({ name: '', status: 'NOT_BOOKED' });
  const [savingDriver, setSavingDriver] = useState(false);
  const driverDirty = driverName.trim() !== driverBaseline.name || driverStatus !== driverBaseline.status;

  const [exportPanel, setExportPanel] = useState<'none' | 'wa' | 'img'>('none');
  const [waText, setWaText] = useState('');
  const [waCopied, setWaCopied] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const opsCanvasRef = useRef<HTMLCanvasElement>(null);

  const isDirty = dirtyItemIds.size > 0 || dirtyTransportIds.size > 0 || parkingDirtyDays.size > 0;

  useEffect(() => {
    if (!itineraryId) return;
    loadData();
    loadDriverPayments();
    loadVendorSummary();
    loadMasterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itineraryId]);

  const loadMasterData = async () => {
    try {
      const res = await api.get('/booking/master-data/all');
      if (res.data.success) setMasterData(res.data.data);
    } catch (err) {
      console.error('Error loading master data:', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await api.get(`/booking/details/${itineraryId}`);
      if (res.data.success) {
        const d = res.data.data;
        setMeta({
          guest: d.lead_guest_name || 'Unknown Guest',
          agency: d.agency_name || 'Unknown Agency',
          pax: d.no_of_pax || 0,
          children: d.no_of_children || 0,
          childrenAges: Array.isArray(d.children_ages) ? d.children_ages : [],
          driverName: d.transportation_handled_by || 'Unassigned',
          travelDate: d.travel_date || '',
          numDays: Array.isArray(d.day_wise_details) ? d.day_wise_details.length : 0,
          totalEstimatedCost: d.total_estimated_cost || 0
        });

        const mapItem = (i: any, item_type: 'prebooked' | 'driver_paid'): LedgerItem => ({
          // Each item's own parking is genuinely persisted on its own row (same
          // parking_cost column Transportation uses) — read it back as-is.
          id: i.id, name: i.name, item_type, item_subtype: i.type, day: i.day || 0,
          cost: i.cost || 0, parking: i.parking || 0, verification_status: i.verification_status,
          vendor: i.vendor || '', booking_status: i.booking_status || 'NOT_BOOKED', due_date: i.due_date,
          activity_id: i.activity_id, meal_id: i.meal_id,
          booking_reference: i.booking_reference, remarks: i.remarks,
          activity_date: i.date || i.activity_date || null,
          calculated_adults: i.calculated_adults || 0, calculated_kids: i.calculated_kids || 0
        });
        const loadedItems: LedgerItem[] = [
          ...(d.prebooked_cost_items || []).map((i: any) => mapItem(i, 'prebooked')),
          ...(d.driver_paid_cost_items || []).map((i: any) => mapItem(i, 'driver_paid'))
        ];

        setAccommodations((d.accommodation_cost_items || []).map((i: any) => ({
          id: i.id, hotel_name: i.hotel_name || 'Unnamed hotel', room_type: i.room_type || '',
          checkin_date: i.checkin_date, checkout_date: i.checkout_date,
          no_of_nights: i.no_of_nights || 0, no_of_rooms: i.no_of_rooms || 0,
          cost: i.cost || 0, vendor: i.vendor || '', booking_status: i.booking_status || 'NOT_BOOKED',
          due_date: i.due_date, booking_reference: i.booking_reference, remarks: i.remarks
        })));

        const loadedTransportDays: TransportDay[] = (d.transportation_cost_items || [])
          .filter((t: any) => (t.day || 0) > 0)
          .map((t: any) => ({ id: t.id, day: t.day, trip: t.trip || `Day ${t.day} Transport`, trip_cost: t.trip_cost || 0, overtime: t.overtime || 0, parking: t.parking || 0 }));

        // Note: a day's Transportation.parking is the aggregate that actually feeds
        // Accounts V3 (see handleSave's rollup) — it can differ from Σ(item.parking)
        // below if it was last set directly in the old modal rather than itemized here.
        // We don't silently reconcile that on load; the two are shown side by side
        // (per-item rows vs. the day subtotal) so a mismatch is visible, not hidden.
        setItems(loadedItems);
        setTransportDays(loadedTransportDays);

        const dName = d.transportation_handled_by || '';
        const dStatus = (d.transportation_cost_items || []).find((t: any) => t.booking_status || t.status)?.booking_status
          || (d.transportation_cost_items || []).find((t: any) => t.status)?.status
          || 'NOT_BOOKED';
        setDriverName(dName);
        setDriverStatus(dStatus);
        setDriverBaseline({ name: dName.trim(), status: dStatus });

        setDirtyItemIds(new Set());
        setDirtyTransportIds(new Set());
        setParkingDirtyDays(new Set());
        setNumDrafts({});
      } else {
        setLoadError('Failed to load booking details.');
      }
    } catch (err) {
      console.error('Error loading booking details:', err);
      setLoadError('Failed to load booking details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDriverPayments = async () => {
    try {
      const res = await api.get(`/accounts-v3/payment-history/${DRIVER_VENDOR_ID}/${itineraryId}`);
      if (res.data.success) setDriverPayments(res.data.data || []);
    } catch (err) {
      console.error('Error loading driver payments:', err);
    }
  };

  const loadVendorSummary = async () => {
    try {
      const res = await api.get(`/accounts-v3/itinerary/${itineraryId}/vendor-summary`);
      if (res.data.success) setVendorSummary(res.data.data.vendors || []);
    } catch (err) {
      console.error('Error loading vendor summary:', err);
    }
  };

  const downloadOpsReportAsPdf = () => {
    // Drawn fresh onto an offscreen canvas (not the visible opsCanvasRef) so
    // the Estimated/variance row — screen-only, per request — is left out of
    // the downloaded PDF without affecting what's shown on the tab.
    const canvas = document.createElement('canvas');
    drawOpsReportImage(canvas, false);
    if (!canvas.width || !canvas.height) return;
    try {
      const imgData = canvas.toDataURL('image/png');
      // Canvas is already drawn at CSS pixel size (style.width/height); convert
      // that to mm 1:1 so the PDF page matches the on-screen report exactly.
      const pageW = canvas.width / (window.devicePixelRatio || 1);
      const pageH = canvas.height / (window.devicePixelRatio || 1);
      const pdf = new jsPDF({
        orientation: pageW > pageH ? 'landscape' : 'portrait',
        unit: 'px',
        format: [pageW, pageH]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, pageW, pageH);
      pdf.save(`WH_${itineraryId}_Operations_Report.pdf`);
    } catch (err) {
      console.error('Error generating operations report PDF:', err);
      alert('Failed to generate the PDF. Please try again.');
    }
  };

  // Day 0 is the "General Items" bucket (no day attached, e.g. per-pax water) —
  // kept as a real entry here (not dropped) and sorted first, ahead of the numbered days.
  const days = useMemo(() => {
    const set = new Set<number>();
    items.forEach(i => set.add(i.day));
    transportDays.forEach(t => set.add(t.day));
    return Array.from(set).filter(d => d >= 0).sort((a, b) => (a === 0 ? -1 : b === 0 ? 1 : a - b));
  }, [items, transportDays]);

  // Grouped by hotel (old Manage Booking nests "day addons" moved into Accommodation —
  // e.g. a floating breakfast — under their hotel rather than listing them as their own
  // stay; 0 nights/0 rooms is the same signal it uses to tell an addon from a real stay),
  // each group sorted by its earliest check-in so the tab reads chronologically.
  const accommodationGroups = useMemo(() => {
    const byHotel = new Map<string, { hotel_name: string; stays: AccommodationItem[]; addons: AccommodationItem[]; earliestCheckin: string }>();
    accommodations.forEach(acc => {
      const key = acc.hotel_name || 'Unassigned';
      if (!byHotel.has(key)) byHotel.set(key, { hotel_name: key, stays: [], addons: [], earliestCheckin: '9999-99-99' });
      const group = byHotel.get(key)!;
      const isAddon = (acc.no_of_nights || 0) === 0 && (acc.no_of_rooms || 0) === 0;
      (isAddon ? group.addons : group.stays).push(acc);
      if (acc.checkin_date && acc.checkin_date < group.earliestCheckin) group.earliestCheckin = acc.checkin_date;
    });
    const groups = Array.from(byHotel.values());
    groups.forEach(g => g.stays.sort((a, b) => (a.checkin_date || '').localeCompare(b.checkin_date || '')));
    return groups.sort((a, b) => a.earliestCheckin.localeCompare(b.earliestCheckin));
  }, [accommodations]);

  // Distinct vendors on this booking, for the per-vendor invoices/files panel.
  const fileVendors = useMemo<VendorRef[]>(() => {
    const map = new Map<string, VendorRef>();
    items.forEach(i => {
      const v = (i.vendor || '').trim();
      if (v) map.set(v.toLowerCase() + '|prebooked_activities', { vendor: v, section: 'prebooked_activities' });
    });
    accommodations.forEach(a => {
      const v = (a.vendor || '').trim();
      if (v) map.set(v.toLowerCase() + '|accommodation', { vendor: v, section: 'accommodation' });
    });
    return Array.from(map.values()).sort((x, y) => x.vendor.localeCompare(y.vendor));
  }, [items, accommodations]);

  // Props for a money cell: plain text field (so up/down arrows and scroll
  // can't change the number), digits + one dot only, never negative.
  const moneyField = (key: string, current: number, commit: (n: number) => void) => ({
    type: 'text' as const,
    inputMode: 'decimal' as const,
    value: numDrafts[key] ?? String(current ?? 0),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const s = e.target.value.replace(/[^\d.]/g, '').replace(/(\.\d*)\./g, '$1');
      setNumDrafts(p => ({ ...p, [key]: s }));
      commit(nonNeg(s));
    },
    onBlur: () => setNumDrafts(p => {
      if (!(key in p)) return p;
      const n = { ...p }; delete n[key]; return n;
    }),
  });

  // Tab walks cost -> parking (same row) -> cost of next row -> parking -> ... ,
  // skipping the type/name/action controls in between. Shift+Tab reverses.
  const ledgerCellKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Tab') return;
    const container = e.currentTarget.closest('[data-ledger]');
    if (!container) return;
    const cells = Array.from(
      container.querySelectorAll<HTMLInputElement>('input[data-cell="cost"],input[data-cell="park"]')
    ).filter(el => el.offsetParent !== null && !el.disabled); // visible layout only, skip prebooked (disabled) cells
    const idx = cells.indexOf(e.currentTarget);
    if (idx === -1) return;
    const next = e.shiftKey ? cells[idx - 1] : cells[idx + 1];
    if (next) {
      e.preventDefault();
      next.focus();
      next.select();
    }
  };

  const updateItem = (id: number, patch: Partial<LedgerItem>) => {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, ...patch } : i)));
    setDirtyItemIds(prev => new Set(prev).add(id));
    if (patch.parking !== undefined) {
      const owner = items.find(i => i.id === id);
      if (owner) setParkingDirtyDays(prev => new Set(prev).add(owner.day));
    }
  };
  const updateTransport = (id: number, patch: Partial<TransportDay>) => {
    setTransportDays(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
    setDirtyTransportIds(prev => new Set(prev).add(id));
  };

  const toggleNoShow = async (item: LedgerItem) => {
    const next = item.verification_status === 'NO_SHOW' ? 'UNSET' : 'NO_SHOW';
    try {
      await api.put(`/booker-ally/verify/${item.id}`, { verification_status: next });
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, verification_status: next === 'UNSET' ? null : 'NO_SHOW' } : i)));
    } catch (err) {
      console.error('Error updating No Show status:', err);
      alert('Failed to update No Show status. Please try again.');
    }
  };

  const removeItem = async (item: LedgerItem) => {
    if (!window.confirm(`Remove "${item.name}" from the ledger? This can't be undone.`)) return;
    try {
      await api.delete(`/booking/delete-item-v2/${item.id}`);
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Error deleting item:', err);
      alert('Failed to delete item. Please try again.');
    }
  };

  // Copy a WhatsApp "BOOKING DETAILS" message for every prebooked item sharing
  // this item's vendor + day — the vendor-booking helper from the old modal.
  const copyVendorWhatsApp = (item: LedgerItem) => {
    const grouped = items.filter(a =>
      a.item_type === 'prebooked' && a.vendor && a.vendor === item.vendor && a.day === item.day
    );
    const lines = grouped.map(a => `* ${(a.calculated_adults || 0) + (a.calculated_kids || 0)} x ${a.name}`);
    const kidsLine = (meta?.children || 0) > 0 ? `\nNo. of Kids : ${meta?.children}` : '';
    const agesLine = (meta?.children || 0) > 0 && meta?.childrenAges?.length ? `\nKids Age : ${meta.childrenAges.join(', ')}` : '';
    const dateStr = item.activity_date
      ? new Date(item.activity_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : `Day ${item.day}`;
    const msg = `*BOOKING DETAILS*\n\nGuest Name : ${meta?.guest || '—'}\nNo. of Adults : ${meta?.pax || 0}${kidsLine}${agesLine}\nActivities:\n${lines.join('\n')}\nDate : ${dateStr}\nRef: WH_${itineraryId}`;
    navigator.clipboard.writeText(msg)
      .then(() => alert('✅ Booking details copied to clipboard!'))
      .catch(() => alert('Could not copy to clipboard.'));
  };

  const saveDriver = async () => {
    if (savingDriver || !driverDirty) return;
    if (isDirty) {
      alert('Save or discard your unsaved ledger changes before saving the driver.');
      return;
    }
    setSavingDriver(true);
    try {
      const res = await api.put(`/booking/update-driver-name/${itineraryId}`, {
        driver_name: driverName.trim(),
        status: driverStatus,
      });
      if (res.data.success) {
        await loadData();
      } else {
        alert('Failed to update driver. Please try again.');
      }
    } catch (err) {
      console.error('Error updating driver:', err);
      alert('Failed to update driver. Please try again.');
    } finally {
      setSavingDriver(false);
    }
  };

  // Reclassify an activity/driver-paid line as accommodation (e.g. floating
  // breakfast, in-hotel spa) so it moves to the Accommodation tab.
  const moveToAccommodation = async (item: LedgerItem) => {
    if (isDirty) {
      alert('Save or discard your unsaved changes before moving an item.');
      return;
    }
    if (!window.confirm(`Move "${item.name}" to the Accommodation tab? Add room / date details there afterwards.`)) return;
    try {
      const res = await api.put('/booking/bulk-update-items-v2', [
        { id: item.id, item_type: 'accommodation', hotel_name: item.name }
      ]);
      if (res.data.success) {
        await loadData();
        setTab('accommodation');
      } else {
        alert('Failed to move item. Please try again.');
      }
    } catch (err) {
      console.error('Error moving item to accommodation:', err);
      alert('Failed to move item. Please try again.');
    }
  };

  // --- Ledger cell renderers, shared by the desktop table and the mobile cards ---
  // Prebooked rows are read-only inline (vouchered amounts shouldn't be typo'd
  // by accident) — they're only editable through the pencil-icon modal.
  // Driver Paid rows stay directly editable since that's the number ops re-key daily.
  const cellType = (item: LedgerItem) => (
    item.item_type === 'driver_paid' ? (
      <select value={item.item_type} onChange={e => updateItem(item.id, { item_type: e.target.value as 'prebooked' | 'driver_paid' })}
        className="text-xs font-semibold rounded-full px-2 py-1 border-0 bg-amber-100 text-amber-700">
        <option value="prebooked">Prebooked</option>
        <option value="driver_paid">Driver paid</option>
      </select>
    ) : (
      <span className="text-xs font-semibold rounded-full px-2 py-1 bg-indigo-100 text-indigo-700" title="Edit via the pencil icon">
        Prebooked
      </span>
    )
  );

  const cellName = (item: LedgerItem, isNoShow: boolean) => {
    const editable = item.item_type === 'driver_paid';
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <input type="text" value={item.name} disabled={!editable}
          onChange={e => updateItem(item.id, { name: e.target.value })}
          title={editable ? undefined : 'Edit via the pencil icon'}
          className={`flex-1 min-w-[120px] px-2 py-1 text-sm border rounded disabled:bg-transparent disabled:cursor-not-allowed disabled:text-gray-500 ${isNoShow ? 'line-through text-gray-400 border-transparent bg-transparent' : 'border-gray-200 sm:border-transparent hover:border-gray-300 focus:border-gray-300'}`} />
        {item.item_type === 'prebooked' && !isNoShow && (
          <span className="text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Voucher</span>
        )}
        {item.vendor && <span className="text-[10px] text-gray-400 truncate max-w-[100px]">· {item.vendor}</span>}
        <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
          item.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
          item.booking_status.startsWith('CANCELED') ? 'bg-gray-200 text-gray-600' :
          item.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-50 text-blue-600'
        }`}>{item.booking_status.replace(/_/g, ' ')}</span>
        <button onClick={() => toggleNoShow(item)}
          className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${isNoShow ? 'text-red-700 bg-red-100 border-red-300' : 'text-gray-400 border-dashed border-gray-300 hover:text-red-600 hover:border-red-400'}`}>
          {isNoShow ? '⚠ No show' : 'No show'}
        </button>
      </div>
    );
  };

  const cellCost = (item: LedgerItem) => {
    const editable = item.item_type === 'driver_paid';
    return (
      <input {...moneyField(`cost-${item.id}`, item.cost, n => updateItem(item.id, { cost: n }))}
        disabled={!editable} title={editable ? undefined : 'Edit via the pencil icon'}
        data-cell="cost" onKeyDown={ledgerCellKeyDown}
        className="w-full sm:w-28 px-2 py-1 text-sm border border-gray-300 rounded text-right disabled:bg-transparent disabled:border-transparent disabled:cursor-not-allowed disabled:text-gray-500" />
    );
  };

  const cellPark = (item: LedgerItem) => {
    const editable = item.item_type === 'driver_paid';
    return (
      <input {...moneyField(`park-${item.id}`, item.parking, n => updateItem(item.id, { parking: n }))}
        disabled={!editable} title={editable ? undefined : 'Edit via the pencil icon'}
        data-cell="park" onKeyDown={ledgerCellKeyDown}
        className="w-full sm:w-24 px-2 py-1 text-sm border border-gray-300 rounded text-right disabled:bg-transparent disabled:border-transparent disabled:cursor-not-allowed disabled:text-gray-500" />
    );
  };

  const rowActions = (item: LedgerItem, day: number) => (
    <div className="flex items-center justify-end whitespace-nowrap">
      {item.item_type === 'prebooked' && item.vendor && !/watersports.*entrance|entrance.*watersports/i.test(item.name) && (
        <button onClick={() => copyVendorWhatsApp(item)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Copy booking details for WhatsApp">
          <MessageCircle className="h-3.5 w-3.5" />
        </button>
      )}
      <button onClick={() => setItemModal({ mode: 'edit', day, item })} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded" title="Vendor, status, due date">
        <Pencil className="h-3.5 w-3.5" />
      </button>
      <button onClick={() => moveToAccommodation(item)} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded" title="Move to Accommodation tab">
        <Hotel className="h-3.5 w-3.5" />
      </button>
      <button onClick={() => removeItem(item)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete item">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    try {
      // Parking is per-item on screen, but never persisted on the item itself — it's a
      // driver expense, not money owed to that stop's own vendor. Each dirty day's items
      // are summed here and rolled into that day's Transportation update instead, same
      // place the old Manage Booking modal already stores parking.
      const transportUpdateMap = new Map<number, { id: number; trip_cost?: number; overtime?: number; parking?: number }>();
      transportDays.filter(t => dirtyTransportIds.has(t.id)).forEach(t => {
        transportUpdateMap.set(t.id, { id: t.id, trip_cost: t.trip_cost, overtime: t.overtime });
      });
      const daysMissingTransport: number[] = [];
      parkingDirtyDays.forEach(day => {
        const transport = transportDays.find(t => t.day === day);
        if (!transport) { daysMissingTransport.push(day); return; }
        const dayItems = items.filter(i => i.day === day);
        const sum = dayItems.reduce((s, i) => s + (i.parking || 0), 0);
        // eslint-disable-next-line no-console
        console.log(`[ManageBooking] Parking rollup — Day ${day}: transport.id=${transport.id}, per-item=`,
          JSON.stringify(dayItems.map(i => ({ id: i.id, name: i.name, parking: i.parking }))),
          `sum=${sum} (was transport.parking=${transport.parking} before this edit)`);
        const existing = transportUpdateMap.get(transport.id) || { id: transport.id };
        transportUpdateMap.set(transport.id, { ...existing, parking: sum });
      });
      if (daysMissingTransport.length > 0) {
        alert(`No Transportation entry exists yet for Day ${daysMissingTransport.join(', ')} — parking on those days can't be saved until a transport entry is added.`);
      }

      const itemUpdates = items.filter(i => dirtyItemIds.has(i.id))
        .map(i => ({ id: i.id, name: i.name, cost: i.cost, item_type: i.item_type, parking: i.parking }));
      const transportUpdates = Array.from(transportUpdateMap.values());
      const payload = [...itemUpdates, ...transportUpdates];
      // eslint-disable-next-line no-console
      console.log('[ManageBooking] Save → payload JSON:', JSON.stringify(payload, null, 2));
      if (payload.length > 0) {
        const res = await api.put('/booking/bulk-update-items-v2', payload);
        // eslint-disable-next-line no-console
        console.log('[ManageBooking] Save ← response JSON:', JSON.stringify(res.data, null, 2));
        if (!res.data.success) {
          alert('Some items failed to save. Check the console for details.');
          console.error('Bulk update errors:', res.data.errors);
        }
      }
      // Raw independent re-fetch (not React state, which may not have re-rendered yet)
      // — this is the authoritative "what does the server actually have right now".
      const editedDays = Array.from(parkingDirtyDays);
      if (editedDays.length > 0) {
        const verify = await api.get(`/booking/details/${itineraryId}`);
        const freshTransport = (verify.data?.data?.transportation_cost_items || [])
          .filter((t: any) => editedDays.includes(t.day))
          .map((t: any) => ({ id: t.id, day: t.day, trip_cost: t.trip_cost, parking: t.parking, overtime: t.overtime }));
        // eslint-disable-next-line no-console
        console.log('[ManageBooking] Server-verified Transportation rows for edited day(s):', JSON.stringify(freshTransport, null, 2));
      }
      setParkingDirtyDays(new Set());
      await loadData();
      // After Save, bring the user back to the header/summary instead of leaving them
      // sitting at the bottom next to the save bar they just used. loadData() just
      // re-rendered the whole ledger with fresh values, which can leave a stale ref to
      // the input the user was just typing in (or the browser's own focused-element
      // handling) still trying to scroll it into view — that fight happens *after* a
      // scrollTo called immediately here, and wins. Blur whatever's focused and wait a
      // couple of frames for that re-render to actually paint before scrolling, so this
      // is the last thing that happens, not something that gets overridden.
      (document.activeElement as HTMLElement | null)?.blur();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!isDirty) return;
    if (window.confirm('Discard all unsaved changes?')) loadData();
  };

  // ---- Totals ----
  const dayTotals = useMemo(() => {
    const map = new Map<number, { driverPaid: number; parking: number; transport: number; total: number }>();
    days.forEach(day => {
      const dayItems = items.filter(i => i.day === day);
      const transport = transportDays.find(t => t.day === day);
      const transportCombined = (transport?.trip_cost || 0) + (transport?.overtime || 0);
      let driverPaid = 0, parking = 0;
      dayItems.forEach(i => {
        if (i.verification_status === 'NO_SHOW') return;
        if (i.item_type === 'driver_paid') driverPaid += i.cost;
        parking += i.parking;
      });
      map.set(day, { driverPaid, parking, transport: transportCombined, total: driverPaid + parking + transportCombined });
    });
    return map;
  }, [days, items, transportDays]);

  const grandTotal = useMemo(() => Array.from(dayTotals.values()).reduce((s, d) => s + d.total, 0), [dayTotals]);
  const totalPaid = useMemo(() => driverPayments.reduce((s, p) => s + (p.payment_amount || 0), 0), [driverPayments]);
  const outstanding = grandTotal - totalPaid;
  const driverBalanceLabel = outstanding > 0
    ? 'Still to pay driver'
    : outstanding < 0
    ? 'Overpaid / advance'
    : 'Settled — nothing due';

  // ---- Vendor expense summary (Operations Report) ----
  // Cost per vendor is sourced from the same already-correct ledger state
  // that drives grandTotal/outstanding above — not re-derived from raw DB
  // columns, since booking_payment_items_v2.estimated_amount doesn't always
  // match the live, edited cost. The Driver row specifically reuses
  // grandTotal/totalPaid verbatim so it can never disagree with the Driver
  // Settlement figures shown elsewhere on this page. Driver's line items are
  // Transportation (one aggregated row) + each driver-paid site visit +
  // Parking (one aggregated row, since dayTotals attributes all parking to
  // the driver regardless of which item it's attached to) — those three
  // always sum to exactly grandTotal. Every other vendor is broken down to
  // its own individual prebooked/accommodation items.
  const UNASSIGNED_VENDOR_LABEL = 'Unassigned Vendor';
  const totalTransport = useMemo(() => Array.from(dayTotals.values()).reduce((s, d) => s + d.transport, 0), [dayTotals]);
  const totalParking = useMemo(() => Array.from(dayTotals.values()).reduce((s, d) => s + d.parking, 0), [dayTotals]);

  const vendorExpenseGroups = useMemo(() => {
    type LineItem = { purpose: string; cost: number; flagged?: boolean };
    const nonDriverItemsByVendor = new Map<string, LineItem[]>();
    items.forEach(i => {
      if (i.item_type !== 'prebooked' || i.verification_status === 'NO_SHOW') return;
      const v = (i.vendor || '').trim() || UNASSIGNED_VENDOR_LABEL;
      const list = nonDriverItemsByVendor.get(v) || [];
      list.push({ purpose: i.name, cost: i.cost || 0 });
      nonDriverItemsByVendor.set(v, list);
    });
    accommodations.forEach(a => {
      const v = (a.vendor || '').trim() || UNASSIGNED_VENDOR_LABEL;
      const list = nonDriverItemsByVendor.get(v) || [];
      // Addons (e.g. a floating breakfast) are always 0 nights/0 rooms —
      // only show the night count for real stays, where it's meaningful.
      const nights = a.no_of_nights || 0;
      const nightsText = nights > 0 ? ` · ${nights} night${nights > 1 ? 's' : ''}` : '';
      list.push({ purpose: `${a.hotel_name}${a.room_type ? ' - ' + a.room_type : ''}${nightsText}`, cost: a.cost || 0 });
      nonDriverItemsByVendor.set(v, list);
    });

    // "Vehicle cost" is how transport charges often actually get entered —
    // as an ad hoc Driver Paid line item — instead of through the dedicated
    // per-day Transport field, which is frequently left blank. Per business
    // definition: Transportation = parking + overtime + vehicle cost (+ the
    // dedicated trip_cost field, when that *is* used). Only Driver-Paid-typed
    // vehicle cost items are folded in here — they're already part of
    // grandTotal via dayTotals, so merging them into this one row doesn't
    // change the Driver's total, just how it's broken down. (Several other
    // itineraries have "vehicle cost" entered as a Prebooked, no-vendor item
    // instead, which is NOT part of grandTotal — folding those in too would
    // change the Driver Settlement total used elsewhere, so that's left
    // alone for now.)
    const isVehicleCostName = (name: string) => /vehicle\s*cost/i.test(name);
    let vehicleCostTotal = 0;
    items.forEach(i => {
      if (i.item_type === 'driver_paid' && i.verification_status !== 'NO_SHOW' && isVehicleCostName(i.name)) {
        vehicleCostTotal += i.cost || 0;
      }
    });

    const driverLineItems: LineItem[] = [];
    const transportationTotal = totalTransport + totalParking + vehicleCostTotal;
    // Always show Transportation — a 0 here is a real signal (cost was never
    // entered for this trip, through any of these mechanisms), not something
    // to silently hide.
    driverLineItems.push({ purpose: 'Transportation', cost: transportationTotal, flagged: transportationTotal === 0 });
    items.forEach(i => {
      if (i.item_type !== 'driver_paid' || i.verification_status === 'NO_SHOW' || isVehicleCostName(i.name)) return;
      driverLineItems.push({ purpose: i.name, cost: i.cost || 0 });
    });

    // Fixed display order: Accommodation vendors, then Driver (transportation
    // + onsite tickets), then every other named Prebooked vendor, with
    // catch-all/generic vendors (Wynder Ops, Unassigned Vendor) last.
    const categoryRank = (v: VendorSummary): number => {
      if (v.vendor_id === DRIVER_VENDOR_ID) return 1;
      if (v.vendor_name === 'Wynder Ops' || v.vendor_name === UNASSIGNED_VENDOR_LABEL) return 3;
      if (v.item_types.includes('ACCOMMODATION')) return 0;
      return 2;
    };

    return vendorSummary
      .slice()
      .sort((a, b) => categoryRank(a) - categoryRank(b) || a.vendor_name.localeCompare(b.vendor_name))
      .map(v => {
        const isDriver = v.vendor_id === DRIVER_VENDOR_ID;
        const lineItems = isDriver ? driverLineItems : (nonDriverItemsByVendor.get(v.vendor_name) || []);
        const totalCost = isDriver ? grandTotal : lineItems.reduce((s, li) => s + li.cost, 0);
        const paid = isDriver ? totalPaid : v.total_paid;
        return {
          vendor_id: v.vendor_id,
          vendor_name: v.vendor_name,
          lineItems,
          total_cost: totalCost,
          total_paid: paid,
          pending: Math.round((totalCost - paid) * 100) / 100
        };
      });
  }, [vendorSummary, items, accommodations, totalTransport, totalParking, grandTotal, totalPaid]);

  // Arrival / departure dates for the header.
  const fmtTripDate = (dt: Date | null) =>
    dt && !isNaN(dt.getTime()) ? dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const arrivalDate = meta?.travelDate ? new Date(meta.travelDate) : null;
  const tripDays = meta?.numDays || (days.length ? Math.max(...days) : 0);
  const departureDate = arrivalDate && !isNaN(arrivalDate.getTime()) && tripDays > 0
    ? new Date(arrivalDate.getTime() + (tripDays - 1) * 86400000)
    : null;

  // ---- Driver Report exports (unchanged logic from before) ----
  const buildWhatsAppText = () => {
    const lines = [`*WH_${itineraryId} — Driver Report*`, `Driver: ${meta?.driverName || 'Unassigned'}`, ''];
    days.forEach(day => {
      const dayItems = items.filter(i => i.day === day && i.verification_status !== 'NO_SHOW');
      const noShowItems = items.filter(i => i.day === day && i.verification_status === 'NO_SHOW');
      const transport = transportDays.find(t => t.day === day);
      const transportCombined = (transport?.trip_cost || 0) + (transport?.overtime || 0);
      lines.push(`*Day ${day}: ${dayItems.map(i => i.name).join(', ')}*`);
      dayItems.forEach(i => {
        let line = `* ${i.name}`;
        if (i.item_type === 'driver_paid') { if (i.cost > 0) line += ` - ${formatCurrency(i.cost)}`; }
        else line += ` - Voucher`;
        if (i.parking > 0) line += ` (Parking ${formatCurrency(i.parking)})`;
        lines.push(line);
      });
      noShowItems.forEach(i => lines.push(`* ~${i.name}~ - No Show`));
      if (transportCombined > 0) { lines.push('————————'); lines.push(`Transportation: ${formatCurrency(transportCombined)}`); }
      lines.push('————————————————');
      lines.push('');
    });
    lines.push('*Driver Settlement*');
    lines.push(`Owed to driver: ${formatCurrency(grandTotal)}`);
    driverPayments.forEach(p => lines.push(`Paid ${p.payment_date}: ${formatCurrency(p.payment_amount)}`));
    lines.push(`Paid to driver (total): ${formatCurrency(totalPaid)}`);
    lines.push('————————');
    lines.push(
      outstanding > 0
        ? `Still to pay driver: ${formatCurrency(outstanding)}`
        : outstanding < 0
        ? `Overpaid / advance with driver: ${formatCurrency(-outstanding)} ✅`
        : `Settled — nothing due ✅`
    );
    return lines.join('\n');
  };

  const openWhatsApp = () => { setWaText(buildWhatsAppText()); setExportPanel('wa'); };
  const copyWaText = async () => {
    try {
      await navigator.clipboard.writeText(waText);
      setWaCopied(true);
      setTimeout(() => setWaCopied(false), 1800);
    } catch {
      const el = document.getElementById('waTextArea') as HTMLTextAreaElement | null;
      el?.focus(); el?.select();
    }
  };

  const drawOpsReportImage = (canvas: HTMLCanvasElement | null, includeEstimate: boolean) => {
    if (!canvas) return;
    const measureCtx = canvas.getContext('2d');
    if (!measureCtx) return;

    const W = 860;
    const PURPOSE_FONT = '500 10px Arial, sans-serif';
    const LINE_H = 12, ROW_MIN_H = 20, ROW_PAD = 8;
    const TABLE_HEADER_H = 24, HEADER_H = 64, TOTAL_ROW_H = 26;

    // Column layout: Vendor | Item/Purpose | Cost | Total Expenses | Paid | Pending.
    const colVendor = 16, colVendorDiv = 148;
    const colPurpose = 158, colCostRight = 512, colCostDiv = 522;
    const colTotalRight = 636, colTotalDiv = 646;
    const colPaidRight = 752, colPaidDiv = 762;
    const colPendRight = W - 16;
    const purposeMaxW = colCostRight - 70 - colPurpose;

    // Wrap (not truncate) the purpose text — full clarity over compactness.
    // Measured with the same ctx/font the row will actually be drawn with.
    measureCtx.font = PURPOSE_FONT;
    const wrapText = (text: string, maxWidth: number): string[] => {
      const words = text.split(' ');
      const lines: string[] = [];
      let current = '';
      words.forEach(word => {
        const test = current ? `${current} ${word}` : word;
        if (measureCtx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      });
      if (current) lines.push(current);
      return lines.length ? lines : [''];
    };

    const groups = vendorExpenseGroups.map(g => {
      const rawRows = g.lineItems.length ? g.lineItems : [{ purpose: '(no items)', cost: 0 }];
      const rows = rawRows.map(li => {
        const lines = wrapText(li.purpose, purposeMaxW);
        return { ...li, lines, h: Math.max(ROW_MIN_H, lines.length * LINE_H + ROW_PAD) };
      });
      return { ...g, rows, groupH: rows.reduce((s, r) => s + r.h, 0) };
    });
    const gridH = groups.reduce((s, g) => s + g.groupH, 0);
    // header + heading(16 gap+20) + table header + grouped rows + grand total row + (estimate/variance row, if included) + bottom pad
    const H = HEADER_H + 16 + 20 + TABLE_HEADER_H + gridH + TOTAL_ROW_H + (includeEstimate ? TOTAL_ROW_H : 0) + 20;

    const DPR = window.devicePixelRatio || 1;
    canvas.width = W * DPR; canvas.height = H * DPR;
    canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(DPR, DPR);
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#111827'; ctx.font = '700 17px Arial, sans-serif';
    ctx.fillText(`WH_${itineraryId} — Operations Report`, 16, 12);
    ctx.font = '500 12px Arial, sans-serif'; ctx.fillStyle = '#4B5563';
    ctx.fillText(`${meta?.guest || ''}${meta?.agency ? ' · ' + meta.agency : ''}`, 16, 34);
    if (arrivalDate && !isNaN(arrivalDate.getTime())) {
      ctx.font = '500 11px Arial, sans-serif'; ctx.fillStyle = '#6B7280';
      ctx.fillText(
        `${fmtTripDate(arrivalDate)} → ${fmtTripDate(departureDate)}${tripDays > 0 ? ` · ${tripDays} days` : ''}`,
        16, 50
      );
    }
    if (includeEstimate) {
      ctx.textAlign = 'right'; ctx.font = '600 11px Arial, sans-serif'; ctx.fillStyle = '#4B5563';
      ctx.fillText(`Est: ${formatCurrency(meta?.totalEstimatedCost || 0)}`, W - 16, 16);
      ctx.textAlign = 'left';
    }

    let y = HEADER_H + 16;
    ctx.font = '700 12px Arial, sans-serif'; ctx.fillStyle = '#6B7280'; ctx.textAlign = 'left';
    ctx.fillText('VENDOR EXPENSE SUMMARY', 16, y);
    y += 20;

    const tableTop = y;
    ctx.fillStyle = '#F3F4F6';
    ctx.fillRect(16, y, W - 32, TABLE_HEADER_H);
    ctx.font = '700 9px Arial, sans-serif'; ctx.fillStyle = '#4B5563';
    ctx.textAlign = 'left'; ctx.fillText('VENDOR', colVendor + 4, y + 8);
    ctx.fillText('ITEM / PURPOSE', colPurpose, y + 8);
    ctx.textAlign = 'right';
    ctx.fillText('COST', colCostRight, y + 8);
    ctx.fillText('TOTAL EXPENSES', colTotalRight, y + 8);
    ctx.fillText('PAID', colPaidRight, y + 8);
    ctx.fillText('PENDING', colPendRight - 4, y + 8);
    y += TABLE_HEADER_H;

    const pendingLabel = (p: number) => {
      if (p > 0) return { text: formatCurrency(p), color: '#B91C1C' };
      if (p < 0) return { text: `${formatCurrency(-p)} Overpaid`, color: '#B45309' };
      return { text: 'Settled', color: '#15803D' };
    };

    let sumCost = 0, sumPaid = 0, sumPending = 0;
    groups.forEach((g, gi) => {
      sumCost += g.total_cost; sumPaid += g.total_paid; sumPending += g.pending;
      const groupTop = y;
      const groupH = g.groupH;

      ctx.fillStyle = gi % 2 === 0 ? '#ffffff' : '#F9FAFB';
      ctx.fillRect(16, groupTop, W - 32, groupH);

      let ry = groupTop;
      g.rows.forEach(r => {
        ctx.font = PURPOSE_FONT; ctx.fillStyle = '#374151'; ctx.textAlign = 'left';
        r.lines.forEach((line, li) => ctx.fillText(line, colPurpose, ry + 5 + li * LINE_H));
        ctx.textAlign = 'right';
        if (r.flagged) {
          // Not just "legitimately zero" like a no-cost site visit — this is
          // a data gap worth the reader's attention, so it's called out
          // rather than blending in as a plain "—".
          ctx.font = '700 9px Arial, sans-serif'; ctx.fillStyle = '#B91C1C';
          ctx.fillText('⚠ Rp 0 — not entered', colCostRight, ry + 5);
        } else {
          ctx.fillStyle = '#111827';
          ctx.fillText(r.cost > 0 ? formatCurrency(r.cost) : '—', colCostRight, ry + 5);
        }
        ctx.textAlign = 'left';
        if (r !== g.rows[g.rows.length - 1]) {
          ctx.strokeStyle = '#EDEFF2';
          ctx.beginPath(); ctx.moveTo(colPurpose - 6, ry + r.h); ctx.lineTo(colCostDiv, ry + r.h); ctx.stroke();
        }
        ry += r.h;
      });

      // Outer border + vertical dividers for the whole group, and the
      // vendor name / group totals centered vertically within it.
      ctx.strokeStyle = '#D1D5DB';
      ctx.strokeRect(16, groupTop, W - 32, groupH);
      [colVendorDiv, colCostDiv, colTotalDiv, colPaidDiv].forEach(x => {
        ctx.beginPath(); ctx.moveTo(x, groupTop); ctx.lineTo(x, groupTop + groupH); ctx.stroke();
      });

      const centerY = groupTop + groupH / 2 - 6;
      ctx.font = '700 11px Arial, sans-serif'; ctx.fillStyle = '#111827'; ctx.textAlign = 'left';
      let vname = g.vendor_name;
      while (ctx.measureText(vname).width > colVendorDiv - colVendor - 10 && vname.length > 1) vname = vname.slice(0, -1);
      if (vname !== g.vendor_name) vname += '…';
      ctx.fillText(vname, colVendor + 4, centerY);

      ctx.textAlign = 'right';
      ctx.fillText(formatCurrency(g.total_cost), colTotalRight, centerY);
      ctx.fillText(formatCurrency(g.total_paid), colPaidRight, centerY);
      const gp = pendingLabel(g.pending);
      ctx.font = '700 10px Arial, sans-serif'; ctx.fillStyle = gp.color;
      ctx.fillText(gp.text, colPendRight - 4, centerY + 1);
      ctx.textAlign = 'left';

      y += groupH;
    });

    // Outer border around the whole grouped grid
    ctx.strokeStyle = '#D1D5DB';
    ctx.strokeRect(16, tableTop + TABLE_HEADER_H, W - 32, y - (tableTop + TABLE_HEADER_H));

    ctx.fillStyle = '#F3F4F6';
    ctx.fillRect(16, y, W - 32, TOTAL_ROW_H);
    ctx.strokeStyle = '#D1D5DB';
    ctx.strokeRect(16, y, W - 32, TOTAL_ROW_H);
    ctx.font = '700 12px Arial, sans-serif'; ctx.fillStyle = '#111827'; ctx.textAlign = 'left';
    ctx.fillText('GRAND TOTAL', colVendor + 4, y + 7);
    ctx.textAlign = 'right';
    ctx.fillText(formatCurrency(sumCost), colTotalRight, y + 7);
    ctx.fillText(formatCurrency(sumPaid), colPaidRight, y + 7);
    const totalPending = pendingLabel(sumPending);
    ctx.font = '700 11px Arial, sans-serif'; ctx.fillStyle = totalPending.color;
    ctx.fillText(totalPending.text, colPendRight - 4, y + 7);
    ctx.textAlign = 'left';
    y += TOTAL_ROW_H;

    if (includeEstimate) {
      // Estimated vs Actual variance — same "Est" figure shown on the Booking
      // Management page's total_estimated_cost, compared against the Actual
      // (GRAND TOTAL) above. Screen-only: left out of the downloaded PDF.
      const estimated = meta?.totalEstimatedCost || 0;
      const variance = sumCost - estimated;
      ctx.fillStyle = '#F9FAFB';
      ctx.fillRect(16, y, W - 32, TOTAL_ROW_H);
      ctx.strokeStyle = '#D1D5DB';
      ctx.strokeRect(16, y, W - 32, TOTAL_ROW_H);
      ctx.font = '600 11px Arial, sans-serif'; ctx.fillStyle = '#4B5563'; ctx.textAlign = 'left';
      ctx.fillText(`Estimated Cost: ${formatCurrency(estimated)}`, colVendor + 4, y + 7);
      ctx.textAlign = 'right';
      ctx.fillStyle = variance > 0 ? '#B91C1C' : variance < 0 ? '#15803D' : '#4B5563';
      ctx.font = '700 11px Arial, sans-serif';
      const varianceText = variance === 0
        ? 'On budget'
        : `${formatCurrency(Math.abs(variance))} ${variance > 0 ? 'over' : 'under'} estimate`;
      ctx.fillText(varianceText, colPendRight - 4, y + 7);
      ctx.textAlign = 'left';
    }
  };

  const drawDriverReportImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dayPalette = ['#CFF2F0', '#FCEAD1', '#E4E4EC', '#DCF3D2', '#DCE6FB', '#F6DCE9'];
    const ROW_H = 26, HEADER_H = 60, DAY_GAP = 6, W = 740;
    const colDay = 14, colDayW = 60;
    const colName = 84, colFeeRight = 486, colParkRight = 588, colTrans = 596, colTransW = 130;

    type Row = { name: string; isDriverPaid: boolean; total: number; parking: number };
    const dayBlocks: { label: string; rows: Row[]; transport: number }[] = [];
    let activitiesTotal = 0, parkingTotal = 0, transportTotal = 0;

    days.forEach(day => {
      const transport = transportDays.find(t => t.day === day);
      const transportCombined = (transport?.trip_cost || 0) + (transport?.overtime || 0);
      transportTotal += transportCombined;
      const rows: Row[] = items.filter(i => i.day === day && i.verification_status !== 'NO_SHOW').map(i => {
        if (i.item_type === 'driver_paid') activitiesTotal += i.cost;
        parkingTotal += i.parking;
        return { name: i.name, isDriverPaid: i.item_type === 'driver_paid', total: i.cost, parking: i.parking };
      });
      dayBlocks.push({ label: `Day ${day}`, rows: rows.length ? rows : [{ name: '(no items)', isDriverPaid: false, total: 0, parking: 0 }], transport: transportCombined });
    });

    const gridHeight = dayBlocks.reduce((sum, d) => sum + d.rows.length * ROW_H + DAY_GAP, 0);
    const paymentLines = driverPayments.length;
    const TOTALS_ROW_H = 30;
    // 16 gap + settlement header(20) + owed(22) + paid label(16) + n*16 + paid-total(26) + settle bar(36)
    const summaryH = 16 + 20 + 22 + 16 + paymentLines * 16 + 26 + 36;
    const H = HEADER_H + gridHeight + TOTALS_ROW_H + 14 + summaryH + 20;

    const DPR = window.devicePixelRatio || 1;
    canvas.width = W * DPR; canvas.height = H * DPR;
    canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(DPR, DPR);
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = '#111827'; ctx.font = '700 17px Arial, sans-serif';
    ctx.fillText(`WH_${itineraryId} — Driver Report`, 16, 12);
    ctx.font = '500 12px Arial, sans-serif'; ctx.fillStyle = '#4B5563';
    ctx.fillText(`${meta?.guest || ''} · Driver: ${meta?.driverName || 'Unassigned'}`, 16, 34);
    ctx.fillStyle = '#92700A'; ctx.font = 'italic 500 10.5px Arial, sans-serif';
    ctx.fillText('Prebooked activities are shown as VOUCHER — no cash to collect for those', 16, 50);

    let y = HEADER_H;
    dayBlocks.forEach((d, di) => {
      const blockH = d.rows.length * ROW_H;
      const color = dayPalette[di % dayPalette.length];
      ctx.fillStyle = color;
      ctx.fillRect(colDay, y, colDayW, blockH);
      ctx.fillRect(colTrans, y, colTransW, blockH);
      ctx.fillStyle = '#111827'; ctx.font = '700 11px Arial, sans-serif';
      ctx.fillText(d.label, colDay + 6, y + 8);
      ctx.font = '600 9px Arial, sans-serif'; ctx.fillStyle = '#4B5563';
      ctx.fillText('TRANSPORT', colTrans + 8, y + 4);
      ctx.font = '700 12px Arial, sans-serif'; ctx.fillStyle = '#111827';
      ctx.fillText(formatCurrency(d.transport), colTrans + 8, y + 15);

      d.rows.forEach((r, ri) => {
        const ry = y + ri * ROW_H;
        ctx.fillStyle = ri % 2 === 0 ? '#ffffff' : '#F9FAFB';
        ctx.fillRect(colName, ry, colTrans - colName, ROW_H);
        ctx.strokeStyle = '#E5E7EB';
        ctx.strokeRect(colName, ry, colTrans - colName, ROW_H);
        ctx.fillStyle = '#111827'; ctx.font = '500 12px Arial, sans-serif'; ctx.textAlign = 'left';
        let name = r.name;
        while (ctx.measureText(name).width > colFeeRight - colName - 100 && name.length > 1) name = name.slice(0, -1);
        if (name !== r.name) name += '…';
        ctx.fillText(name, colName + 8, ry + 7);
        ctx.textAlign = 'right';
        if (r.isDriverPaid) {
          ctx.font = '600 12px Arial, sans-serif'; ctx.fillStyle = '#111827';
          ctx.fillText(r.total > 0 ? formatCurrency(r.total) : '—', colFeeRight, ry + 7);
        } else {
          ctx.font = 'italic 700 10px Arial, sans-serif'; ctx.fillStyle = '#92700A';
          ctx.fillText('VOUCHER', colFeeRight, ry + 8);
        }
        ctx.font = '500 11px Arial, sans-serif'; ctx.fillStyle = '#6B7280';
        ctx.fillText(r.parking > 0 ? formatCurrency(r.parking) : '–', colParkRight, ry + 8);
        ctx.textAlign = 'left';
      });
      y += blockH + DAY_GAP;
    });

    y += 4;
    ctx.fillStyle = '#F3F4F6';
    ctx.fillRect(colName, y, colTrans + colTransW - colName, TOTALS_ROW_H);
    ctx.strokeStyle = '#E5E7EB';
    ctx.strokeRect(colName, y, colTrans + colTransW - colName, TOTALS_ROW_H);
    ctx.fillStyle = '#111827'; ctx.font = '700 12px Arial, sans-serif'; ctx.textAlign = 'left';
    ctx.fillText('TOTAL', colName + 8, y + 9);
    ctx.font = '700 12px Arial, sans-serif'; ctx.textAlign = 'right';
    ctx.fillText(formatCurrency(activitiesTotal), colFeeRight, y + 9);
    ctx.fillText(formatCurrency(parkingTotal), colParkRight, y + 9);
    ctx.fillText(formatCurrency(transportTotal), colTrans + colTransW - 8, y + 9);
    ctx.textAlign = 'left';
    y += TOTALS_ROW_H + 10;

    ctx.strokeStyle = '#E5E7EB';
    ctx.beginPath(); ctx.moveTo(16, y); ctx.lineTo(W - 16, y); ctx.stroke();
    y += 16;

    ctx.font = '700 12px Arial, sans-serif'; ctx.fillStyle = '#6B7280'; ctx.textAlign = 'left';
    ctx.fillText('DRIVER SETTLEMENT', 16, y);
    y += 20;
    ctx.font = '600 12px Arial, sans-serif'; ctx.fillStyle = '#111827'; ctx.textAlign = 'left';
    ctx.fillText('Owed to driver', 16, y);
    ctx.textAlign = 'right'; ctx.font = '700 13px Arial, sans-serif';
    ctx.fillText(formatCurrency(grandTotal), W - 16, y);
    y += 22;

    ctx.textAlign = 'left'; ctx.font = '500 11px Arial, sans-serif'; ctx.fillStyle = '#6B7280';
    ctx.fillText('Paid to driver', 16, y);
    y += 16;
    driverPayments.forEach(p => {
      ctx.font = '500 11px Arial, sans-serif'; ctx.fillStyle = '#6B7280'; ctx.textAlign = 'left';
      ctx.fillText(`  ${p.payment_date}`, 16, y);
      ctx.font = '500 11px Arial, sans-serif'; ctx.textAlign = 'right';
      ctx.fillText(formatCurrency(p.payment_amount), W - 16, y);
      y += 16;
    });
    ctx.font = '600 12px Arial, sans-serif'; ctx.fillStyle = '#111827'; ctx.textAlign = 'left';
    ctx.fillText('Paid to driver (total)', 16, y);
    ctx.textAlign = 'right'; ctx.font = '700 13px Arial, sans-serif';
    ctx.fillText(formatCurrency(totalPaid), W - 16, y);
    y += 26;

    ctx.fillStyle = outstanding > 0 ? '#FEE2E2' : outstanding < 0 ? '#FEF3C7' : '#DCFCE7';
    ctx.fillRect(16, y - 6, W - 32, 30);
    ctx.font = '700 13px Arial, sans-serif';
    ctx.fillStyle = outstanding > 0 ? '#B91C1C' : outstanding < 0 ? '#B45309' : '#15803D';
    ctx.textAlign = 'left';
    ctx.fillText(driverBalanceLabel, 24, y + 3);
    ctx.textAlign = 'right'; ctx.font = '700 14px Arial, sans-serif';
    ctx.fillText(formatCurrency(Math.abs(outstanding)), W - 24, y + 3);
    ctx.textAlign = 'left';
  };

  useEffect(() => {
    if (exportPanel === 'img') drawDriverReportImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportPanel, items, transportDays, driverPayments]);

  useEffect(() => {
    if (tab === 'ops_report') drawOpsReportImage(opsCanvasRef.current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, vendorExpenseGroups, meta]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-600">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <p className="text-gray-700 mb-4">{loadError}</p>
          <button onClick={loadData} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-5">

        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-4 sm:px-5 py-4 flex items-start justify-between gap-3 flex-wrap text-white">
            <div className="flex items-start gap-3">
              <button onClick={() => navigate('/booking')} className="p-2 text-white/80 hover:bg-white/10 rounded-lg" title="Back to Booking Management">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-lg sm:text-xl font-bold" style={soraFont}>WH_{itineraryId} — Manage Booking</h1>
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-white/20 rounded">V4</span>
                </div>
                <p className="text-sm text-teal-50">
                  {meta?.guest} · {meta?.agency} · {meta?.pax} adults{meta && meta.children > 0 ? ` + ${meta.children} children` : ''}
                </p>
                {arrivalDate && !isNaN(arrivalDate.getTime()) && (
                  <p className="text-sm text-teal-100">
                    {fmtTripDate(arrivalDate)} → {fmtTripDate(departureDate)}{tripDays > 0 ? ` · ${tripDays} days` : ''}
                  </p>
                )}
                <p className="text-sm text-teal-100">Driver: {meta?.driverName}</p>
              </div>
            </div>
            <div className="text-right text-xs min-w-[190px]">
              <p className="text-[10px] uppercase tracking-wide text-teal-100 font-semibold mb-1">Driver Settlement</p>
              <div className="flex justify-between text-teal-50">
                <span>Owed to driver</span>
                <span className="font-mono font-semibold ml-3">{formatCurrency(grandTotal)}</span>
              </div>
              <div className="flex justify-between text-teal-50">
                <span>Paid to driver</span>
                <span className="font-mono font-semibold ml-3">{formatCurrency(totalPaid)}</span>
              </div>
              <div className={`mt-1 pt-1 border-t border-white/25 flex justify-between font-bold ${
                outstanding > 0 ? 'text-red-200' : outstanding < 0 ? 'text-amber-200' : 'text-emerald-200'
              }`}>
                <span>{driverBalanceLabel}</span>
                <span className="font-mono ml-3">{formatCurrency(Math.abs(outstanding))}</span>
              </div>
            </div>
          </div>
          <div className="flex border-t border-gray-200">
            <button onClick={() => setTab('ledger')}
              className={`px-4 sm:px-5 py-2.5 text-sm font-semibold ${tab === 'ledger' ? 'text-teal-700 border-b-2 border-teal-600' : 'text-gray-500'}`}>
              Activities &amp; Transport
            </button>
            <button onClick={() => setTab('accommodation')}
              className={`px-4 sm:px-5 py-2.5 text-sm font-semibold ${tab === 'accommodation' ? 'text-teal-700 border-b-2 border-teal-600' : 'text-gray-500'}`}>
              Accommodation ({accommodations.length})
            </button>
            {canViewOpsReport && (
              <button onClick={() => setTab('ops_report')}
                className={`px-4 sm:px-5 py-2.5 text-sm font-semibold ${tab === 'ops_report' ? 'text-teal-700 border-b-2 border-teal-600' : 'text-gray-500'}`}>
                Operations Report
              </button>
            )}
          </div>
        </div>

        {tab === 'ledger' && (
          <div data-ledger className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <h2 className="font-semibold text-gray-900" style={soraFont}>Activities &amp; Transport</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-indigo-500 inline-block"></span>Prebooked = Voucher on report</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500 inline-block"></span>Driver Paid = real amount</span>
              </div>
            </div>

            <div className="px-4 sm:px-5 py-2.5 border-b border-gray-200 flex items-center gap-4">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Show</span>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input type="radio" name="item-filter" checked={itemFilter === 'all'} onChange={() => setItemFilter('all')}
                  className="text-teal-600 focus:ring-teal-500" />
                All items
              </label>
              <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                <input type="radio" name="item-filter" checked={itemFilter === 'driver_paid'} onChange={() => setItemFilter('driver_paid')}
                  className="text-teal-600 focus:ring-teal-500" />
                Driver paid only
              </label>
            </div>

            <div className="px-4 sm:px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Driver</label>
                <input type="text" value={driverName} onChange={e => setDriverName(e.target.value)}
                  placeholder="Driver name"
                  className="w-56 px-2 py-1.5 text-sm border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Transport status</label>
                <select value={driverStatus} onChange={e => setDriverStatus(e.target.value)}
                  className="px-2 py-1.5 text-sm border border-gray-300 rounded">
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <button onClick={saveDriver} disabled={!driverDirty || savingDriver}
                className="px-3 py-1.5 text-xs font-bold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {savingDriver ? 'Saving…' : 'Save driver'}
              </button>
              <p className="text-[11px] text-gray-400 basis-full sm:basis-auto">
                Applies the name &amp; status to every Driver Paid and Transport item.
              </p>
            </div>

            {days.length === 0 && <div className="p-8 text-center text-gray-500 text-sm">No activities or transport recorded for this booking yet.</div>}

            {days.map(day => {
              const isGeneral = day === 0;
              const dt = dayTotals.get(day)!;
              const transport = transportDays.find(t => t.day === day);
              const dayItems = items
                .filter(i => i.day === day)
                .filter(i => itemFilter === 'all' || i.item_type === 'driver_paid');
              return (
                <div key={day} className="border-b-4 border-gray-300 last:border-b-0">
                  <div className="bg-gray-50 px-4 sm:px-5 py-3 flex flex-wrap items-center gap-4">
                    <span className="font-bold text-gray-900 text-sm" style={soraFont}>
                      {isGeneral ? '📋 General Items' : `Day ${day}`}
                    </span>
                    <button onClick={() => setItemModal({ mode: 'create', day })}
                      className="flex items-center gap-1 text-xs font-semibold text-teal-700 border border-dashed border-teal-300 rounded-lg px-2.5 py-1.5 hover:bg-teal-50">
                      <Plus className="h-3.5 w-3.5" /> Add item
                    </button>

                    <div className="ml-auto flex flex-wrap items-center justify-end gap-4">
                      {!isGeneral && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Transport</label>
                          {transport ? (
                            <input {...moneyField(`trip-${transport.id}`, transport.trip_cost, n => updateTransport(transport.id, { trip_cost: n }))}
                              className="w-28 px-2 py-1 text-sm border border-gray-300 rounded text-right" />
                          ) : <span className="text-xs text-gray-400 italic">No transport entry</span>}
                        </div>
                      )}
                      {!isGeneral && transport && (
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-500">Overtime</label>
                          <input {...moneyField(`ot-${transport.id}`, transport.overtime, n => updateTransport(transport.id, { overtime: n }))}
                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded text-right" />
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Day subtotal</p>
                        <p className="text-sm font-bold text-teal-700" style={monoFont}>{formatCurrency(dt.total)}</p>
                      </div>
                    </div>
                  </div>
                  <p className="px-4 sm:px-5 pt-2 text-[11px] text-gray-400">
                    Driver Paid {formatCurrency(dt.driverPaid)}{!isGeneral ? ` + Transport ${formatCurrency(dt.transport)}` : ''} + Parking {formatCurrency(dt.parking)}
                  </p>

                  {/* Desktop / tablet: table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400">
                          <th className="px-4 sm:px-5 py-2 font-semibold w-32">Type</th>
                          <th className="py-2 font-semibold">Item</th>
                          <th className="py-2 font-semibold text-right w-32">Cost</th>
                          <th className="py-2 font-semibold text-right w-28">Parking</th>
                          <th className="py-2 font-semibold w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dayItems.map(item => {
                          const isNoShow = item.verification_status === 'NO_SHOW';
                          const isPrebooked = item.item_type === 'prebooked';
                          return (
                            <tr key={item.id} className={`border-t border-gray-100 ${isNoShow ? 'bg-red-50' : isPrebooked ? 'bg-gray-50' : ''}`}>
                              <td className="px-4 sm:px-5 py-2">{cellType(item)}</td>
                              <td className="py-2 pr-2">{cellName(item, isNoShow)}</td>
                              <td className="py-2 text-right">{cellCost(item)}</td>
                              <td className="py-2 text-right">{cellPark(item)}</td>
                              <td className="py-2 pr-4 sm:pr-5">{rowActions(item, day)}</td>
                            </tr>
                          );
                        })}
                        {dayItems.length === 0 && (
                          <tr><td colSpan={5} className="px-4 sm:px-5 py-3 text-xs text-gray-400 italic">
                            {itemFilter === 'driver_paid' ? 'No driver-paid items this day.' : 'No activities this day — transport/overtime only.'}
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Phone: stacked cards */}
                  <div className="sm:hidden divide-y divide-gray-100">
                    {dayItems.length === 0 && (
                      <p className="px-4 py-3 text-xs text-gray-400 italic">
                        {itemFilter === 'driver_paid' ? 'No driver-paid items this day.' : 'No activities this day — transport/overtime only.'}
                      </p>
                    )}
                    {dayItems.map(item => {
                      const isNoShow = item.verification_status === 'NO_SHOW';
                      const isPrebooked = item.item_type === 'prebooked';
                      return (
                        <div key={item.id} className={`px-4 py-3 space-y-2 ${isNoShow ? 'bg-red-50' : isPrebooked ? 'bg-gray-50' : ''}`}>
                          <div className="flex items-center justify-between gap-2">
                            {cellType(item)}
                            {rowActions(item, day)}
                          </div>
                          {cellName(item, isNoShow)}
                          <div className="grid grid-cols-2 gap-3">
                            <label className="block text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                              Cost
                              <span className="mt-1 block">{cellCost(item)}</span>
                            </label>
                            <label className="block text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                              Parking
                              <span className="mt-1 block">{cellPark(item)}</span>
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'accommodation' && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900" style={soraFont}>Accommodation</h2>
              <p className="text-xs text-gray-500 mt-0.5">Adding new accommodation still needs the hotel-picker flow from the existing Manage Booking.</p>
            </div>
            {accommodations.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No accommodation recorded for this booking.</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {accommodationGroups.map(group => (
                  <div key={group.hotel_name + group.earliestCheckin} className="p-4 sm:p-5">
                    <p className="font-semibold text-gray-900 mb-2">{group.hotel_name}</p>
                    <div className="space-y-2">
                      {group.stays.map(acc => (
                        <div key={acc.id} className="flex items-center justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-sm text-gray-700 font-medium">{acc.room_type} · {acc.no_of_nights} nights · {acc.no_of_rooms} rooms</p>
                            <p className="text-xs text-gray-400">{acc.checkin_date || '—'} → {acc.checkout_date || '—'} {acc.vendor && `· ${acc.vendor}`}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                              acc.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
                              acc.booking_status.startsWith('CANCELED') ? 'bg-gray-200 text-gray-600' :
                              acc.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-50 text-blue-600'
                            }`}>{acc.booking_status.replace(/_/g, ' ')}</span>
                            <p className="font-mono font-semibold text-gray-900">{formatCurrency(acc.cost)}</p>
                            <button onClick={() => setAccModalItem(acc)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg">
                              <Pencil className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Addons nested under their hotel — same amber convention as the old Manage Booking modal */}
                      {group.addons.map(addon => (
                        <div key={addon.id} className="ml-3 border border-amber-200 bg-amber-50 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="px-1.5 py-0.5 bg-amber-200 text-amber-800 text-[10px] font-bold rounded flex-shrink-0">ADDON</span>
                            <div className="min-w-0">
                              <p className="text-sm text-gray-800 font-medium truncate">{addon.room_type}</p>
                              <p className="text-xs text-gray-400">{addon.checkin_date || '—'} {addon.vendor && `· ${addon.vendor}`}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${
                              addon.booking_status === 'PAYMENT_COMPLETED' ? 'bg-green-100 text-green-700' :
                              addon.booking_status.startsWith('CANCELED') ? 'bg-gray-200 text-gray-600' :
                              addon.booking_status === 'PENDING_PAYMENT' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-50 text-blue-600'
                            }`}>{addon.booking_status.replace(/_/g, ' ')}</span>
                            <p className="font-mono font-semibold text-gray-900">{formatCurrency(addon.cost)}</p>
                            <button onClick={() => setAccModalItem(addon)} className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-100 rounded-lg">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'ops_report' && canViewOpsReport && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="font-semibold text-gray-900" style={soraFont}>Operations Report</h2>
                <p className="text-xs text-gray-500">Full vendor-level cost/payment breakdown for this trip. Visible to Admin and Accounts only.</p>
              </div>
              <button onClick={downloadOpsReportAsPdf}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                <Download className="h-3.5 w-3.5" /> Download as PDF
              </button>
            </div>
            <div className="p-4 sm:p-5 bg-gray-100 overflow-x-auto flex justify-center">
              <canvas ref={opsCanvasRef} className="shadow-md" />
            </div>
          </div>
        )}

        {/* Vendor Invoices & Files — invoices are per vendor, not per item */}
        <VendorFilesPanel itineraryId={itineraryId!} vendors={fileVendors} />

        {/* Driver Report */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button onClick={() => setReportsOpen(o => !o)} className="w-full flex items-center justify-between p-4 sm:p-5 text-left">
            <div>
              <h2 className="font-semibold text-gray-900" style={soraFont}>Driver Report</h2>
              <p className="text-xs text-gray-500">Generated live from the ledger above — no separate report-building step.</p>
            </div>
            <div className="text-gray-400">
              {reportsOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </button>

          {reportsOpen && (
          <div className="border-t border-gray-100 p-4 sm:p-5">
          <div className="flex flex-wrap gap-2 mb-3">
            <button disabled title="Not available yet — needs a backend endpoint to store and serve the report by code"
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-200 rounded-lg text-gray-400 cursor-not-allowed">
              <LinkIcon className="h-3.5 w-3.5" /> Get shareable link
            </button>
            <button onClick={openWhatsApp}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-300 rounded-lg text-gray-700 hover:border-green-500 hover:text-green-700">
              <MessageCircle className="h-3.5 w-3.5" /> Copy for WhatsApp
            </button>
            <button onClick={() => setExportPanel('img')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-gray-300 rounded-lg text-gray-700 hover:border-blue-500 hover:text-blue-700">
              <ImageIcon className="h-3.5 w-3.5" /> Excel-style image
            </button>
          </div>

          {exportPanel === 'wa' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-3">
              <textarea id="waTextArea" readOnly value={waText} rows={10} className="w-full text-xs font-mono border border-gray-300 rounded p-2 bg-white resize-y" />
              <button onClick={copyWaText} className={`mt-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg ${waCopied ? 'bg-green-100 text-green-700' : 'bg-green-600 text-white hover:bg-green-700'}`}>
                <Copy className="h-3.5 w-3.5" /> {waCopied ? 'Copied!' : 'Copy to WhatsApp'}
              </button>
            </div>
          )}

          {exportPanel === 'img' && (
            <>
              <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 mb-2 overflow-x-auto flex justify-center">
                <canvas ref={canvasRef} className="shadow-md" />
              </div>
              <p className="text-[11px] text-gray-400 mb-1">Right-click the image → Save Image As… to download it.</p>
            </>
          )}

          <div className="border-t border-gray-100 pt-3 mt-1">
            <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Payments Received (Accounts V3)</h3>
            {driverPayments.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No payments recorded for the driver yet.</p>
            ) : (
              <div className="space-y-1">
                {driverPayments.map((p, idx) => (
                  <div key={idx} className="flex justify-between text-xs text-gray-600">
                    <span>{p.payment_date} · {p.bank_account || '—'}</span>
                    <span className="font-mono font-semibold text-gray-900">{formatCurrency(p.payment_amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden text-sm">
              <div className="flex justify-between px-3 py-1.5 text-gray-600">
                <span>Owed to driver</span>
                <span className="font-mono font-semibold text-gray-900">{formatCurrency(grandTotal)}</span>
              </div>
              <div className="flex justify-between px-3 py-1.5 text-gray-600 border-t border-gray-100">
                <span>Paid to driver</span>
                <span className="font-mono font-semibold text-gray-900">{formatCurrency(totalPaid)}</span>
              </div>
              <div className={`flex justify-between px-3 py-2 font-bold border-t border-gray-200 ${
                outstanding > 0 ? 'bg-red-50 text-red-700' : outstanding < 0 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
              }`}>
                <span>{driverBalanceLabel}</span>
                <span className="font-mono">{formatCurrency(Math.abs(outstanding))}</span>
              </div>
            </div>
          </div>
          </div>
          )}
        </div>
      </div>

      {/* Save bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.15)] z-40">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className={`flex items-center gap-2 text-sm ${isDirty ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
            <span className={`w-2 h-2 rounded-full ${isDirty ? 'bg-amber-500' : 'bg-gray-300'}`}></span>
            {isDirty ? 'Unsaved changes' : 'No unsaved changes'}
          </div>
          <div className="flex gap-2">
            <button onClick={handleDiscard} disabled={!isDirty}
              className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:border-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed">
              Discard
            </button>
            <button onClick={handleSave} disabled={!isDirty || saving}
              className="min-w-[130px] flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed">
              <CheckCircle className="h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>

      {itemModal && (
        <ItemModal
          state={itemModal}
          itineraryId={itineraryId!}
          masterData={masterData}
          onClose={() => setItemModal(null)}
          onSaved={() => { setItemModal(null); loadData(); }}
        />
      )}
      {accModalItem && (
        <AccommodationModal
          item={accModalItem}
          itineraryId={itineraryId!}
          onClose={() => setAccModalItem(null)}
          onSaved={() => { setAccModalItem(null); loadData(); }}
        />
      )}
    </div>
  );
};

export default ManageBooking;
